import { useTranslation } from "react-i18next";
import { Award, Sparkles, ShieldCheck, AlertTriangle } from "lucide-react";

/**
 * Displays a small confidence chip on a prescribed workshop card.
 * Tiers (backend-driven, see /app/backend/confidence.py):
 *   - strong: track record of successful executions
 *   - good:   decent usage, mixed execution
 *   - new:    never or barely prescribed yet : cold-start
 *   - weak:   often prescribed but rarely executed → coaches skip it
 */
const TIER_META = {
  strong: {
    icon: Award,
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    ring: "ring-emerald-200",
  },
  good: {
    icon: ShieldCheck,
    bg: "bg-blue-100",
    text: "text-blue-800",
    ring: "ring-blue-200",
  },
  new: {
    icon: Sparkles,
    bg: "bg-purple-100",
    text: "text-purple-800",
    ring: "ring-purple-200",
  },
  weak: {
    icon: AlertTriangle,
    bg: "bg-amber-100",
    text: "text-amber-800",
    ring: "ring-amber-200",
  },
};

export default function ConfidenceBadge({ confidence, compact = false }) {
  const { t } = useTranslation();
  if (!confidence?.tier) return null;
  const meta = TIER_META[confidence.tier] || TIER_META.good;
  const Icon = meta.icon;
  const label = t(`confidence.tier_${confidence.tier}`, confidence.tier);
  const total = confidence.total_realized ?? 0;
  const exec = Math.round((confidence.execution_rate ?? 0) * 100);
  const title = t("confidence.tooltip", {
    total,
    exec,
    prescribed: confidence.total_prescribed ?? 0,
  });

  if (compact) {
    return (
      <span
        title={title}
        data-testid={`confidence-badge-${confidence.tier}`}
        className={`inline-flex items-center gap-1 ${meta.bg} ${meta.text} text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${meta.ring}`}
      >
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  }

  return (
    <div
      title={title}
      data-testid={`confidence-badge-${confidence.tier}`}
      className={`inline-flex items-center gap-1.5 ${meta.bg} ${meta.text} text-xs font-bold px-2.5 py-1 rounded-full ring-1 ${meta.ring}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="uppercase tracking-wider">{label}</span>
      {total > 0 && (
        <span className="font-mono text-[10px] font-normal opacity-70">
          {total}× · {exec}%
        </span>
      )}
    </div>
  );
}
